/*
  Сотрудники организации — §2.1, §3.1.5.

  Роль определяет не только меню, но и то, что человек вправе делать: оператор
  коммуникаций видит реестр, но заявлений не подаёт. Поэтому при заведении
  учётной записи рядом с каждой ролью стоит объяснение, а не только название.
*/

import { useCallback, useState } from 'react'

import { api } from '../api/client.js'
import { fieldErrors, invalidClass as invalid } from '../api/fieldErrors.js'
import { dateTime } from '../api/format.js'
import { LockedForm } from './AccessContext.jsx'
import { Field, Loading, Notice } from './Common.jsx'
import PageBar from './PageBar.jsx'
import Dropdown from './Select.jsx'
import usePagedList from './usePagedList.js'
import useSingleFlight from './useSingleFlight.js'

/*
  Подписи ролей - обещание, которое кабинет обязан держать. 18.09.2026 у юриста
  строка изменилась вслед за правами: настройки, из которых собирается заявление,
  ему открылись, а сотрудники и линия связи остались за руководителем. Пока
  строка говорила «настройки организации - нет», руководитель заводил юриста и
  считал, что реквизиты тот не тронет.
*/
const ROLES = [
  ['head', 'Руководитель', 'Видит деньги, заводит сотрудников и может делать всю работу сам'],
  [
    'lawyer',
    'Юрист',
    'Реестр, документы, подача, стадии и настройки, из которых собирается ' +
      'заявление: реквизиты, карточка организации, тарифы, давность, участки. ' +
      'Сотрудники и линия связи - нет',
  ],
  [
    'communications_operator',
    'Оператор коммуникаций',
    'Только реестр: разговоры и споры по суммам, без процессуальных действий',
  ],
  ['agent', 'Агент сопровождения', 'Ведёт вашу очередь наравне с юристом'],
]

// Названия ролей для выпадающего списка: он берёт готовый набор «код -
// название», а объяснение к роли живёт под формой отдельной строкой.
const ROLE_TITLES = Object.fromEntries(ROLES.map(([value, title]) => [value, title]))

export default function UsersSection({ onError }) {
  // 21.10.2026: страницы перечня сотрудников режет сервер (требование владельца), под
  // перечнем - общая панель с числом записей. Заведение сотрудника перечитывает страницу.
  const load = useCallback((window) => api.users(window), [])
  const { rows: users, error: listError, reload, bar } = usePagedList(load)
  const [added, setAdded] = useState(null)
  const [error, setError] = useState(null)
  // Причина у самого поля (ревью 30.10.2026, аудит 26.10.2026, причина 6): ошибка стояла под
  // формой, но поле с ошибкой не отмечалось.
  const problems = fieldErrors(error)
  const single = useSingleFlight()
  // Роль хранится состоянием, а не читается из формы: свой список - разметка
  // кабинета, а не поле браузера, и в `FormData` он не попадает.
  const [role, setRole] = useState('lawyer')

  async function create(event) {
    event.preventDefault()
    const element = event.currentTarget
    const form = new FormData(element)
    // Второе нажатие, пока первое в пути, пропускается: два одинаковых сотрудника
    // не заводятся (аудит 26.10.2026, причина 4).
    await single.run(async () => {
      setError(null)
      try {
        const user = await api.createUser({
          email: form.get('email'),
          full_name: form.get('full_name'),
          role,
          password: form.get('password'),
        })
        setAdded(user)
        reload()
        element.reset()
        setRole('lawyer')
      } catch (failure) {
        setError(failure)
        if (onError) onError(null)
      }
    })
  }

  return (
    <section className="card">
      <Notice error={listError} />
      {added ? (
        <Notice kind="ok">
          {added.full_name} заведён как «{added.role_title}». Передайте пароль лично - по почте он
          не отправляется.
        </Notice>
      ) : null}

      {!users && !listError ? <Loading /> : null}

      {users && users.length > 0 ? (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Сотрудник</th>
                <th>Почта</th>
                <th>Роль</th>
                <th>Последний вход</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.user_id}>
                  <td>{user.full_name}</td>
                  <td data-label="Почта">{user.email}</td>
                  <td data-label="Роль">{user.role_title}</td>
                  <td className="muted" data-label="Последний вход">
                    {user.last_login_at ? dateTime(user.last_login_at) : 'ещё не входил'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {users ? <PageBar {...bar} what="сотрудников" /> : null}

      <h3 className="section__title">Завести сотрудника</h3>

      {/*
        Справка о ролях стоит НАД формой, а не под ней (09.09.2026). Под формой
        её читали после того, как роль уже выбрана, - то есть не читали вовсе.
        Роль определяет не только меню, но и то, что человек вправе делать, и
        менять её потом значит заходить сюда второй раз.
      */}
      <ul className="roles">
        {ROLES.map(([value, title, explanation]) => (
          <li key={value}>
            <strong>{title}</strong> - {explanation}
          </li>
        ))}
      </ul>

      <LockedForm busy={single.busy} onSubmit={create}>
        <div className="row">
          {/* Поля в строке с кнопкой: причина не под полем (правило 6г - она удлинила бы поле
              и сбила линию), а в плашке под формой; само поле отмечено рамкой и `aria-invalid`. */}
          <Field label="Фамилия, имя и отчество" width="m" className={invalid(problems.full_name)}>
            <input
              className="field__input"
              name="full_name"
              required
              aria-invalid={problems.full_name ? 'true' : undefined}
            />
          </Field>
          <Field label="Рабочая почта" width="m" className={invalid(problems.email)}>
            <input
              className="field__input"
              name="email"
              type="email"
              required
              aria-invalid={problems.email ? 'true' : undefined}
            />
          </Field>
          <div className={`field field--s${problems.role ? ' field--invalid' : ''}`}>
            <span className="field__label" id="role-label">
              Роль
            </span>
            <Dropdown value={role} onChange={setRole} options={ROLE_TITLES} labelledBy="role-label" />
          </div>
          <Field label="Первый пароль" width="s" className={invalid(problems.password)}>
            <input
              className="field__input"
              name="password"
              type="text"
              minLength={12}
              required
              aria-invalid={problems.password ? 'true' : undefined}
            />
          </Field>
          <button className="button" type="submit" disabled={single.busy}>
            {single.busy ? 'Заводим…' : 'Завести'}
          </button>
        </div>
        <p className="field__hint">
          Не меньше двенадцати знаков. Пароль передаётся сотруднику лично: по почте Арбитр ЖКХ его
          не отправляет.
        </p>
        {/* Ошибка заведения - под самой формой, а не над перечнем сотрудников: при длинном
            перечне она оказывалась за экраном от кнопки (аудит 26.10.2026, причина 6). */}
        <Notice error={error} />
      </LockedForm>
    </section>
  )
}
